import { useParams, Link, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import BookingButton from "@/components/BookingButton";
import ContactForm from "@/components/ContactForm";
import CTASection from "@/components/CTASection";
import { findService, SERVICES, SERVICE_CATEGORIES, DELIVERY_MODEL } from "@/data/content";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const DELIVERABLES = [
  "Structured discovery workshop and written scope",
  "Baseline assessment of the relevant environment",
  "Prioritised recommendations with effort and impact scoring",
  "Implementation with continuous progress reporting",
  "Handover documentation and operational runbooks",
  "Post-engagement support window",
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = findService(slug);
  if (!s) return <Navigate to="/services" replace />;

  const category = SERVICE_CATEGORIES.find((c) => c.id === s.category);
  const related = SERVICES.filter((x) => x.category === s.category && x.slug !== s.slug).slice(0, 3);

  return (
    <>
      <SEO title={s.name} description={s.summary} />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="dm-overline text-[var(--dm-emerald)]">{category?.name}</div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-6 leading-[1.05]">{s.name}</h1>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">{s.tagline}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <BookingButton label="Request Technical Assessment" meetingType="consultation" variant="primary" testId={`svc-${s.slug}-assessment`} />
              <BookingButton label="Schedule Discovery Call" meetingType="discovery" variant="ghost" testId={`svc-${s.slug}-discovery`} />
            </div>
          </div>

          {/* Custom-proposal panel — no fixed pricing displayed */}
          <div className="lg:col-span-4 flex items-end">
            <div className="border border-white/15 p-6 bg-white/[0.04] w-full">
              <div className="dm-overline text-white/60">Engagement approach</div>
              <div className="font-serif text-2xl mt-3 leading-snug">Custom proposals</div>
              <div className="text-sm text-white/70 mt-3 leading-relaxed">
                Each proposal is tailored to your infrastructure, business goals and project scope after an initial technical discovery.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8">
            <div className="dm-overline">Overview</div>
            <h2 className="font-serif text-4xl text-[var(--dm-navy)] mt-4 leading-tight">{s.summary}</h2>
            <p className="text-[var(--dm-muted)] mt-6 leading-relaxed">
              Every {s.name} engagement is led by a senior consultant. Work is scoped and priced only after a structured technical discovery — we do not publish package pricing because every enterprise environment is different.
            </p>

            <div className="mt-14">
              <div className="dm-overline">What you get</div>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {DELIVERABLES.map((d) => (
                  <li key={d} className="flex gap-3 text-sm text-[var(--dm-navy)]">
                    <Check className="w-4 h-4 mt-1 text-[var(--dm-emerald)]" /> {d}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-14">
              <div className="dm-overline">How we work</div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                {DELIVERY_MODEL.slice(0, 6).map((p) => (
                  <div key={p.step} className="border-t border-[var(--dm-border)] pt-4">
                    <div className="font-mono text-xs text-[var(--dm-emerald)]">{p.step}</div>
                    <div className="font-serif text-xl text-[var(--dm-navy)] mt-2">{p.title}</div>
                    <div className="text-sm text-[var(--dm-muted)] mt-2 leading-relaxed">{p.body}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14 flex flex-wrap gap-3">
              <BookingButton label="Request Proposal" meetingType="consultation" variant="primary" testId={`svc-${s.slug}-proposal`} />
              <BookingButton label="Talk to a Database Specialist" meetingType="discovery" variant="outline" testId={`svc-${s.slug}-specialist`} />
            </div>

            {related.length > 0 && (
              <div className="mt-14">
                <div className="dm-overline">Related services</div>
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="border border-[var(--dm-border)] p-5 hover:border-[var(--dm-emerald)]">
                      <div className="font-serif text-[var(--dm-navy)]">{r.name}</div>
                      <div className="text-xs text-[var(--dm-muted)] mt-2">{r.tagline}</div>
                      <div className="text-xs text-[var(--dm-emerald)] mt-3 inline-flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-[var(--dm-surface)] border border-[var(--dm-border)] p-8">
              <div className="dm-overline">Enquire</div>
              <div className="font-serif text-2xl text-[var(--dm-navy)] mt-3 mb-6">Talk to a specialist</div>
              <ContactForm sourcePage={`service:${s.slug}`} compact defaultService={s.name} />
            </div>
          </aside>
        </div>
      </section>

      <CTASection />
    </>
  );
}
